import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  Chip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ModalLayout from "components/ModalLayout";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

const SosRequests = () => {
  const [status, setStatus] = useState("active");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRequests = async (nextStatus, nextSearch) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest.get(endPoints.GET_SOS_REQUESTS, {
        status: nextStatus,
        search: nextSearch || undefined,
      });
      setItems(response?.data?.items || []);
    } catch (err) {
      setError("Failed to load SOS requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(status, search);
  }, [status, search]);

  const handleTabChange = (_event, value) => {
    setStatus(value);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleShowLocation = (row) => {
    const latitude = row?.latitude ?? row?.location?.latitude;
    const longitude = row?.longitude ?? row?.location?.longitude;
    if (!latitude || !longitude) return;
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenDetail = (row) => {
    setSelectedRequest(row);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedRequest(null);
  };

  const handleResolve = async () => {
    if (!selectedRequest?.id) return;
    setActionLoading(true);
    try {
      await apiRequest.put(
        `${endPoints.UPDATE_SOS_REQUEST_STATUS}/${selectedRequest.id}/status`,
        { status: "resolved" }
      );
      handleCloseDetail();
      fetchRequests(status, search);
    } catch (err) {
      setError("Failed to resolve SOS request.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  SOS Requests
                </MDTypography>
              </MDBox>
              <MDBox pt={1} px={1}>
                <MDBox
                  pt={1}
                  px={1}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Tabs
                    value={status}
                    onChange={handleTabChange}
                    sx={{
                      minHeight: 44,
                      "& .MuiTabs-flexContainer": { gap: 1 },
                      "& .MuiTabs-indicator": { display: "none" },
                    }}
                  >
                    <Tab
                      label="Active"
                      value="active"
                      sx={{
                        minHeight: 40,
                        borderRadius: 999,
                        px: 3,
                        textTransform: "none",
                        fontWeight: 700,
                        color: status === "active" ? "#fff" : "#c62828",
                        bgcolor: status === "active" ? "#c62828" : "rgba(198,40,40,0.08)",
                      }}
                    />
                    <Tab
                      label="Resolved"
                      value="resolved"
                      sx={{
                        minHeight: 40,
                        borderRadius: 999,
                        px: 3,
                        textTransform: "none",
                        fontWeight: 700,
                        color: status === "resolved" ? "#fff" : "#2e7d32",
                        bgcolor: status === "resolved" ? "#2e7d32" : "rgba(46,125,50,0.08)",
                      }}
                    />
                  </Tabs>
                  <TextField
                    label="Search"
                    variant="outlined"
                    margin="normal"
                    placeholder="Search by order ref, driver, or note"
                    value={search}
                    onChange={handleSearchChange}
                    style={{ width: "300px" }}
                  />
                </MDBox>

                {loading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                )}

                {!loading && error && (
                  <Typography color="error" sx={{ py: 2 }}>
                    {error}
                  </Typography>
                )}

                {!loading && !error && (
                  <TableContainer>
                    <Table>
                      {/* <TableHead> */}
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Order Ref</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Driver</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                      {/* </TableHead> */}
                      <TableBody>
                        {items.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No SOS requests found.
                            </TableCell>
                          </TableRow>
                        )}
                        {items.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row?.order?.order_ref || row?.order_id || "-"}</TableCell>
                            <TableCell>{row?.driver?.full_name || "-"}</TableCell>
                            <TableCell>{row?.driver?.contact || "-"}</TableCell>
                            <TableCell>{row?.note || row?.note || "-"}</TableCell>
                            <TableCell>
                              {row?.latitude && row?.longitude
                                ? `${row.latitude}, ${row.longitude}`
                                : row?.location?.latitude && row?.location?.longitude
                                ? `${row.location.latitude}, ${row.location.longitude}`
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {row?.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={row?.status || "-"}
                                sx={{
                                  fontWeight: 700,
                                  textTransform: "capitalize",
                                  bgcolor: row?.status === "resolved" ? "#e8f5e9" : "#ffebee",
                                  color: row?.status === "resolved" ? "#2e7d32" : "#c62828",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box
                                component="button"
                                onClick={() => handleOpenDetail(row)}
                                style={{
                                  border: "none",
                                  background: "#1e88e5",
                                  color: "#fff",
                                  padding: "6px 12px",
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                              >
                                Details
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <ModalLayout
        open={detailOpen}
        onClose={handleCloseDetail}
        title="SOS Request Details"
        subtitle="SOS"
        maxWidth={760}
        contentSx={{ maxHeight: "60vh", overflowY: "auto" }}
        footerSx={{ p: 2, pt: 1.5 }}
        actions={
          <>
            <MDButton
              variant="gradient"
              color="error"
              onClick={handleResolve}
              disabled={actionLoading || selectedRequest?.status === "resolved"}
            >
              Mark Resolved
            </MDButton>
            <MDButton variant="gradient" color="info" onClick={handleCloseDetail}>
              Close
            </MDButton>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              Order Ref
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedRequest?.order?.order_ref || selectedRequest?.order_id || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedRequest?.status || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              Driver
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedRequest?.driver?.full_name || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              Contact
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedRequest?.driver?.contact || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Note
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedRequest?.note || selectedRequest?.note || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Dropoff Address
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedRequest?.order?.dropoff_address || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              SOS Location
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedRequest?.latitude && selectedRequest?.longitude
                ? `${selectedRequest.latitude}, ${selectedRequest.longitude}`
                : selectedRequest?.location?.latitude && selectedRequest?.location?.longitude
                ? `${selectedRequest.location.latitude}, ${selectedRequest.location.longitude}`
                : "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              Reported At
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedRequest?.createdAt
                ? new Date(selectedRequest.createdAt).toLocaleString()
                : "-"}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box
              component="button"
              onClick={() => handleShowLocation(selectedRequest)}
              style={{
                border: "none",
                background: "#d32f2f",
                color: "#fff",
                padding: "10px 16px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Show Location
            </Box>
          </Grid>
        </Grid>
      </ModalLayout>
      <Footer />
    </DashboardLayout>
  );
};

export default SosRequests;
